import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Link, Paragraph } from '@digdir/designsystemet-react';
import type { WorkEnvironmentDocumentation } from '../../../api/workEnvironmentTypes';
import { onlyGaps, requirementCards, STATUS_COLORS } from '../inspectionReportView';
import { ReportSection, StatusTag } from './ReportSection';
import classes from '../InspectionReport.module.css';

const K = 'workEnvironmentInspection';

type Props = {
  report: WorkEnvironmentDocumentation;
  showOnlyGaps: boolean;
  /** Where a plan for the area is made, for those who make plans. Never in the PDF. */
  planLink?: (areaKey: string) => string;
};

/** Part 2: each requirement in the law, what the team did, what shows it, and the status */
export const RequirementsPart = ({ report, showOnlyGaps, planLink }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const cards = onlyGaps(requirementCards(report, t, i18n.language), showOnlyGaps);
  return (
    <ReportSection
      id="report-requirements"
      part={2}
      title={t(`${K}.parts.requirements`)}
      intro={t(`${K}.parts.requirementsIntro`)}
      newPage
    >
      <ol className={classes.requirements}>
        {cards.map((card) => (
          <li key={card.key} className={classes.requirement} data-status={card.status}>
            <div className={classes.requirementTop}>
              <span className={classes.requirementTitle}>
                <span className="ds-label">
                  {card.number}. {t(`${K}.requirements.${card.key}.title`)}
                </span>
                <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                  {t(`${K}.requirements.${card.key}.reference`)}
                </span>
              </span>
              <StatusTag label={t(`${K}.status.${card.status}`)} color={STATUS_COLORS[card.status]} />
            </div>
            <dl className={classes.twoColumns}>
              <div className={classes.fact}>
                <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                  {t(`${K}.labels.how`)}
                </dt>
                <dd className="ds-paragraph" data-size="sm">
                  {card.how}
                </dd>
              </div>
              <div className={classes.fact}>
                <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                  {t(`${K}.labels.evidence`)}
                </dt>
                <dd className="ds-paragraph" data-size="sm">
                  {card.evidence}
                </dd>
              </div>
            </dl>
            {card.gap && (
              <Paragraph data-size="sm" className={classes.gap}>
                {t(`${K}.labels.gap`, { text: card.gap })}
              </Paragraph>
            )}
            {planLink && card.areasLeft.length > 0 && (
              <ul className={classes.gapLinks} data-print="false">
                {card.areasLeft.map((area) => (
                  <li key={area.key}>
                    <Link asChild data-size="sm">
                      <RouterLink to={planLink(area.key)}>
                        {t('workEnvironmentAreasLeft.planFor', { name: area.name })}
                      </RouterLink>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </ReportSection>
  );
};
